/**
 * Minimal rich text editor built on contenteditable + document.execCommand.
 *
 * Yes, execCommand is technically deprecated — but it works in every
 * modern browser, produces clean HTML for email use, and skips a 100KB
 * WYSIWYG dependency. Toolbar covers the 90% case: bold, italic,
 * underline, bullet + numbered lists, quote, and link.
 */
import { Bold, ChevronDown, Highlighter, Italic, Link as LinkIcon, List, ListOrdered, Palette, Quote, Type, Underline } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  disabled = false,
}: Props): JSX.Element {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef<string>(value);

  // Sync external value → editor, but only when it actually differs
  // (otherwise the cursor jumps to the start on every keystroke).
  useEffect(() => {
    if (!editorRef.current) return;
    if (value !== lastValueRef.current) {
      editorRef.current.innerHTML = value;
      lastValueRef.current = value;
    }
  }, [value]);

  function emit(): void {
    const html = editorRef.current?.innerHTML ?? '';
    lastValueRef.current = html;
    onChange(html);
  }

  function apply(command: string, arg?: string): void {
    if (disabled) return;
    document.execCommand(command, false, arg);
    editorRef.current?.focus();
    emit();
  }

  function addLink(): void {
    if (disabled) return;
    const url = window.prompt('Link URL', 'https://');
    if (!url) return;
    apply('createLink', url);
  }

  const isEmpty = !value || value === '<br>' || value === '<div><br></div>';

  return (
    <div className={'rounded-md border border-mb-border bg-white ' + (disabled ? 'opacity-60' : '')}>
      <div className="flex items-center gap-1 border-b border-mb-border px-2 py-1.5 flex-wrap">
        <ToolbarBtn icon={Bold} label="Bold (Ctrl+B)" onClick={() => apply('bold')} disabled={disabled} />
        <ToolbarBtn icon={Italic} label="Italic (Ctrl+I)" onClick={() => apply('italic')} disabled={disabled} />
        <ToolbarBtn icon={Underline} label="Underline (Ctrl+U)" onClick={() => apply('underline')} disabled={disabled} />
        <ToolbarBtn
          icon={Highlighter}
          label="Highlight"
          onClick={() => apply('hiliteColor', '#FFF3B0')}
          disabled={disabled}
        />
        <Divider />
        <FontDropdown onPick={(f) => apply('fontName', f)} disabled={disabled} />
        <ColorPicker onPick={(c) => apply('foreColor', c)} disabled={disabled} />
        <Divider />
        <ToolbarBtn icon={List} label="Bulleted list" onClick={() => apply('insertUnorderedList')} disabled={disabled} />
        <ToolbarBtn icon={ListOrdered} label="Numbered list" onClick={() => apply('insertOrderedList')} disabled={disabled} />
        <Divider />
        <ToolbarBtn icon={Quote} label="Quote" onClick={() => apply('formatBlock', 'blockquote')} disabled={disabled} />
        <ToolbarBtn icon={LinkIcon} label="Insert link" onClick={addLink} disabled={disabled} />
      </div>
      <div className="relative">
        {isEmpty && placeholder ? (
          <div className="pointer-events-none absolute top-2.5 left-3 text-sm text-mb-text-subtle italic">
            {placeholder}
          </div>
        ) : null}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onKeyDown={(e) => {
            // Ctrl/Cmd + Enter is the send shortcut — let the parent
            // handle it, don't insert a newline here.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') return;
          }}
          className="prose prose-sm max-w-none px-3 py-2.5 text-sm text-mb-text focus:outline-none"
          style={{ minHeight: `${rows * 1.5}em` }}
        />
      </div>
    </div>
  );
}

function ToolbarBtn({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: typeof Bold;
  label: string;
  onClick: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        // Prevent losing focus from the editor when clicking the toolbar
        // (otherwise document.execCommand runs against nothing).
        e.preventDefault();
      }}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="rounded p-1.5 text-mb-text-muted hover:bg-mb-bg-soft hover:text-mb-text disabled:opacity-40"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function Divider(): JSX.Element {
  return <div className="mx-1 h-4 w-px bg-mb-border" />;
}

/**
 * Font-family picker. Uses `document.execCommand('fontName', ...)` which
 * wraps the selection in <font face="…">. Slightly dated markup but it
 * renders correctly in every mail client, which is the point.
 */
const FONT_CHOICES: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Sans', value: 'Arial, sans-serif' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Mono', value: 'Menlo, Monaco, Consolas, monospace' },
  { label: 'Courier', value: '"Courier New", Courier, monospace' },
  { label: 'Times', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
];

function FontDropdown({
  onPick,
  disabled,
}: {
  onPick: (font: string) => void;
  disabled: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title="Font family"
        className="flex items-center gap-1 rounded p-1.5 text-mb-text-muted hover:bg-mb-bg-soft hover:text-mb-text disabled:opacity-40"
      >
        <Type className="h-3.5 w-3.5" />
        <ChevronDown className="h-3 w-3" />
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-20 mt-1 min-w-32 rounded-md border border-mb-border bg-white shadow-md"
          onMouseDown={(e) => e.preventDefault()}
        >
          {FONT_CHOICES.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => {
                onPick(f.value);
                setOpen(false);
              }}
              className="block w-full px-3 py-1.5 text-left text-xs text-mb-text hover:bg-mb-bg-soft"
              style={{ fontFamily: f.value }}
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Text color picker. Fixed palette (12 swatches) rather than a full
 * color wheel — cold email doesn't need Pantone matching, and it keeps
 * the toolbar compact.
 */
const COLOR_CHOICES: ReadonlyArray<string> = [
  '#000000', '#404040', '#7a7a7a', '#B01818',
  '#D97706', '#65A30D', '#0891B2', '#2563EB',
  '#7C3AED', '#C026D3', '#DB2777', '#F5B800',
];

function ColorPicker({
  onPick,
  disabled,
}: {
  onPick: (color: string) => void;
  disabled: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title="Text color"
        className="flex items-center gap-1 rounded p-1.5 text-mb-text-muted hover:bg-mb-bg-soft hover:text-mb-text disabled:opacity-40"
      >
        <Palette className="h-3.5 w-3.5" />
        <ChevronDown className="h-3 w-3" />
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-20 mt-1 rounded-md border border-mb-border bg-white p-2 shadow-md"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="grid grid-cols-4 gap-1">
            {COLOR_CHOICES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  onPick(c);
                  setOpen(false);
                }}
                className="h-6 w-6 rounded border border-mb-border hover:scale-110 transition-transform"
                style={{ backgroundColor: c }}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
